import { Component, OnDestroy, OnInit, ViewChild, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { Subject, forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { DynamicSearchFormComponent } from '@app/dynamic-search-form/dynamic-search-form.component';
import { HttpService } from '@core/services/http.service';
import { DataGridVO } from '@app/shared/custom-component/data-grid/data-grid.model';
import { LoaderService } from '@core/services/loader.service';
import { DataGridComponent } from '@app/shared/custom-component/data-grid/data-grid.component';
import { CommonService } from '@app/shared/services/common.service';
import { formatPaymentReconciliationAmount } from './payment-reconciliation-report.util';

@Component({
  selector: 'app-payment-reconciliation-report',
  templateUrl: './payment-reconciliation-report.component.html',
  styleUrls: ['./payment-reconciliation-report.component.scss'],
  imports : [DynamicSearchFormComponent]
})
export class PaymentReconciliationReportComponent implements OnInit, AfterViewInit, OnDestroy {
  dateRangeError: string = '';
  startDateError: string = '';
  endDateError: string = '';
  lastChangedField: string = '';
  showDateRangeGrid: boolean = false;
  disableSubmitButton: boolean = true;
  private destroy$ = new Subject<void>();
  
  // Dynamic messages loaded from API
  errorMessages: any[] = [];
  toastMessages: any[] = [];
  genericMessages: any[] = [];
  placeholderMessages: any[] = [];
  
  // Message objects for easy lookup by key
  errorMessage: any = {};
  toastMessage: any = {};
  genericMessage: any = {};
  placeholderMessage: any = {};

  @ViewChild(DynamicSearchFormComponent) searchFormComponent!: DynamicSearchFormComponent;
  @ViewChild(DataGridComponent) dataGridComponent!: DataGridComponent;

  // Date range fields configuration for dynamic search form
  dateRangeFields: any[] = [];
  
  // Payment reconciliation data and grid config
  transactionTableData: any[] = [];
  transactionGridConfig!: DataGridVO;
  
  // Column chooser properties
  showColumnChooser = false;
  allColumns = [
    { dataField: 'applicantName', caption: 'Applicant Name', key: 'applicantName', dataType: 'string', disabled: true },
    { dataField: 'licenseNumber', caption: 'License Number', key: 'licenseNumber', dataType: 'string' },
    { dataField: 'applicationType', caption: 'Application Type', key: 'applicationType', dataType: 'string' },
    { dataField: 'amountPaid', caption: 'Amount Paid', key: 'amountPaid', dataType: 'string' },
    { dataField: 'datePaid', caption: 'Date Paid', key: 'datePaid', dataType: 'string' },
    { dataField: 'revenueCodeDescription', caption: 'Revenue Code Description', key: 'revenueCodeDescription', dataType: 'string' },
    { dataField: 'emailAddress', caption: 'Email Address', key: 'emailAddress', dataType: 'string' },
    { dataField: 'transactionId', caption: 'Transaction ID', key: 'transactionId', dataType: 'string' }
  ];
  visibleColumns: { [key: string]: boolean } = {
    applicantName: true,
    licenseNumber: true,
    applicationType: true,
    amountPaid: true,
    datePaid: true,
    revenueCodeDescription: true,
    emailAddress: true,
    transactionId: true
  };
  
  transactionHeaders = [
    { dataField: 'applicantName', caption: 'Applicant Name', allowSearch: { isSearchEnable: true, type: 'string' } },
    { dataField: 'licenseNumber', caption: 'License Number', allowSearch: { isSearchEnable: true, type: 'string' } },
    { dataField: 'applicationType', caption: 'Application Type', allowSearch: { isSearchEnable: true, type: 'string' } },
    { dataField: 'amountPaid', caption: 'Amount Paid', allowSearch: { isSearchEnable: true, type: 'string' } },
    { dataField: 'datePaid', caption: 'Date Paid', allowSearch: { isSearchEnable: true, type: 'string' } },
    { dataField: 'revenueCodeDescription', caption: 'Revenue Code Description', allowSearch: { isSearchEnable: true, type: 'string' } },
    { dataField: 'emailAddress', caption: 'Email Address', allowSearch: { isSearchEnable: true, type: 'string' } },
    { dataField: 'transactionId', caption: 'Transaction ID', allowSearch: { isSearchEnable: true, type: 'string' } }
  ];

  constructor(
    private httpService: HttpService,
    private cd: ChangeDetectorRef,
    private loaderService: LoaderService,
    private commonService: CommonService
  ) {
  }

  ngOnInit(): void {
    // Load messages from API
    this.loadDropdownDataMessages();
  }

  ngAfterViewInit(): void {
    // Subscription will be set up after config is loaded and form is rebuilt
    // See loadDropdownDataMessages() success callback
  }

  subscribeToFormChanges(): void {
    if (this.searchFormComponent && this.searchFormComponent.form) {
      // Subscribe to individual field changes to track which field was changed last
      const startDateControl = this.searchFormComponent.form.get('startDate');
      const endDateControl = this.searchFormComponent.form.get('endDate');
      
      if (startDateControl) {
        startDateControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.lastChangedField = 'startDate';
            // Use setTimeout to ensure the form value is fully updated
            setTimeout(() => {
              this.validateDateRangeValues(this.searchFormComponent.form.value);
            }, 0);
          });
      }
      
      if (endDateControl) {
        endDateControl.valueChanges
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => {
            this.lastChangedField = 'endDate';
            // Use setTimeout to ensure the form value is fully updated
            setTimeout(() => {
              this.validateDateRangeValues(this.searchFormComponent.form.value);
            }, 0);
          });
      }
    }
  }

  validateDateRangeValues(values: any): void {
    // Clear previous errors
    this.dateRangeError = '';
    this.startDateError = '';
    this.endDateError = '';
    
    const startDate = values?.startDate;
    const endDate = values?.endDate;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    // Validate individual dates even if both are not selected yet
    if (startDate) {
      const start = new Date(startDate);
      if (!isNaN(start.getTime())) {
        const startOnly = new Date(start);
        startOnly.setHours(0, 0, 0, 0);
        
        // M0208: Check if start date is in the future
        if (startOnly > today) {
          this.startDateError = this.errorMessage['M0208'] || 'Date cannot be later than the current date.';
          this.disableSubmitButton = true;
          this.cd.detectChanges();
          return;
        }
      }
    }
    
    if (endDate) {
      const end = new Date(endDate);
      if (!isNaN(end.getTime())) {
        const endOnly = new Date(end);
        endOnly.setHours(0, 0, 0, 0);
        
        // M0208: Check if end date is in the future
        if (endOnly > today) {
          this.endDateError = this.errorMessage['M0208'] || 'Date cannot be later than the current date.';
          this.disableSubmitButton = true;
          this.cd.detectChanges();
          return;
        }
      }
    }

    // Check if both dates are selected for cross-field validations
    if (!startDate || !endDate) {
      this.disableSubmitButton = true;
      return;
    }

    // Convert to Date objects
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Validate dates are present and valid
    if (!start || !end || isNaN(start.getTime()) || isNaN(end.getTime())) {
      this.disableSubmitButton = true;
      return;
    }

    // Reset time to compare dates only (not time)
    const startOnly = new Date(start);
    startOnly.setHours(0, 0, 0, 0);
    
    const endOnly = new Date(end);
    endOnly.setHours(0, 0, 0, 0);
    
    console.log('Date Validation:', {
      startDate: startOnly.toISOString(),
      endDate: endOnly.toISOString(),
      startTime: startOnly.getTime(),
      endTime: endOnly.getTime(),
      endLessThanStart: endOnly.getTime() < startOnly.getTime()
    });

    // M0209: Validate end date is not earlier than start date - Always show below end date
    if (endOnly.getTime() < startOnly.getTime()) {
      this.endDateError = this.errorMessage['M0209'] || 'End Date cannot be earlier than the Start Date.';
      this.disableSubmitButton = true;
      console.log('M0209 Error triggered - End date is earlier than start date');
      this.cd.detectChanges();
      return;
    }

    // M0206: Validate 62-day range - Show below the field that was changed last
    const diffTime = Math.abs(endOnly.getTime() - startOnly.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays > 62) {
      const errorMsg = this.errorMessage['M0206'] || 'Date range cannot exceed 62 days.';
      if (this.lastChangedField === 'startDate') {
        this.startDateError = errorMsg;
      } else if (this.lastChangedField === 'endDate') {
        this.endDateError = errorMsg;
      } else {
        // Default to start date if no field was explicitly changed
        this.startDateError = errorMsg;
      }
      this.disableSubmitButton = true;
      this.cd.detectChanges();
      return;
    }

    // All validations passed - enable Submit button
    this.disableSubmitButton = false;
    this.cd.detectChanges();
  }

  isResetEnabled(): boolean {
    if (!this.searchFormComponent || !this.searchFormComponent.form) {
      return false;
    }
    const form = this.searchFormComponent.form;
    return form.get('startDate')?.value !== null || 
           form.get('endDate')?.value !== null;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // Event handler for Reset button
  onDateRangeReset(): void {
    if (this.searchFormComponent && this.searchFormComponent.form) {
      // Reset form completely
      this.searchFormComponent.form.reset({
        startDate: null,
        endDate: null
      });
      
      // Clear the form controls explicitly
      const startDateControl = this.searchFormComponent.form.get('startDate');
      const endDateControl = this.searchFormComponent.form.get('endDate');
      
      if (startDateControl) {
        startDateControl.setValue(null);
        startDateControl.markAsUntouched();
        startDateControl.markAsPristine();
        startDateControl.updateValueAndValidity();
      }
      
      if (endDateControl) {
        endDateControl.setValue(null);
        endDateControl.markAsUntouched();
        endDateControl.markAsPristine();
        endDateControl.updateValueAndValidity();
      }
      
      // CRITICAL FIX: Clear all hidden date inputs in the DOM
      // The dynamic-search-form component uses hidden <input type="date"> elements
      // that don't get cleared by form.reset() alone
      setTimeout(() => {
        const hiddenDateInputs = document.querySelectorAll('input[type="date"]');
        hiddenDateInputs.forEach((input: any) => {
          if (input) {
            input.value = '';
          }
        });
      }, 0);
    }
    this.dateRangeError = '';
    this.startDateError = '';
    this.endDateError = '';
    this.lastChangedField = '';
    this.showDateRangeGrid = false;
    this.disableSubmitButton = true;
    this.transactionTableData = [];
    
    // Force change detection to ensure UI updates
    this.cd.detectChanges();
  }

  // Event handler for Submit button
  onDateRangeSubmit(): void {
    if (!this.searchFormComponent || !this.searchFormComponent.form) {
      return;
    }

    const form = this.searchFormComponent.form;
    
    if (form.invalid) {
      Object.keys(form.controls).forEach(key => {
        form.get(key)?.markAsTouched();
      });
      return;
    }

    if (this.dateRangeError || this.startDateError || this.endDateError) {
      return;
    }

    // Call API to fetch transaction data
    this.fetchTransactionData();
  }

  /**
   * Fetch transaction data from NestJS API
   */
  private fetchTransactionData(): void {
    const formValue = this.searchFormComponent.form.value;
    
    // Extract and format dates
    let startDate = '';
    let endDate = '';

    if (formValue.startDate) {
      startDate = this.formatDateForAPI(formValue.startDate);
    }

    if (formValue.endDate) {
      endDate = this.formatDateForAPI(formValue.endDate);
    }

    if (!startDate || !endDate) {
      return;
    }

    // Show loader
    this.loaderService.show();

    const apiUrl = `/reports/transactions?startDate=${startDate}&endDate=${endDate}`;

    // Call NestJS API endpoint
    this.httpService.get(apiUrl)
      .subscribe({
        next: (response: any) => {
          this.loaderService.hide();
          
          // Handle nested response structure: {status: 'success', data: {success: true, data: [...], totalRecords: 32}}
          const actualData = response?.data?.data || response?.data || [];
          const isSuccess = response?.status === 'success' || response?.data?.success === true;
          
          if (isSuccess && Array.isArray(actualData) && actualData.length > 0) {
            // Map and format the data similar to registration template
            this.transactionTableData = actualData.map((item: any) => ({
              applicantName: item.applicantName || '---',
              licenseNumber: item.licenseNumber || '---',
              applicationType: item.applicationType || '---',
              amountPaid: formatPaymentReconciliationAmount(item.amountPaid, item.isReduction),
              datePaid: this.formatDate(item.datePaid),
              revenueCodeDescription: item.revenueCodeDescription || '---',
              emailAddress: item.emailAddress || '---',
              transactionId: item.transactionId || '---'
            }));

            // Create grid config using makeGridConfig method (same pattern as registration template)
            // Filter headers to show only visible columns
            const visibleHeaders = this.transactionHeaders.filter(
              col => this.visibleColumns[col.dataField]
            );
            this.transactionGridConfig = this.makeGridConfig(
              visibleHeaders,
              this.transactionTableData
            );
            
            // Show the grid
            this.showDateRangeGrid = true;
            this.cd.detectChanges();
          } else {
            // No data found
            this.transactionTableData = [];
            this.showDateRangeGrid = false;
          }
        },
        error: (error: any) => {
          this.loaderService.hide();
          this.transactionTableData = [];
          this.showDateRangeGrid = false;
        }
      });
  }

  /**
   * Helper method to create grid config (same pattern as registration template)
   */
  private makeGridConfig(headers: any[], data: any[]): DataGridVO {
    return DataGridVO.create({
      customHeaders: headers,
      data: Array.isArray(data) ? data : (data ? [data] : []),
      showActionsColumn: false,
      showFiltering: true,
      showSearchRow: true,
      showPaging: true,
      customNoDataText: 'No transaction records found',
      role: 'payment-reconciliation',
      activeTab: 'default',
      visibleColumns: this.visibleColumns
    });
  }

  /**
   * Format date for API (YYYY-MM-DD)
   */
  private formatDateForAPI(date: any): string {
    if (!date) return '';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Format date for display - Show UTC time in readable AM/PM format
   */
  private formatDate(dateStr: any): string {
    if (!dateStr) return '---';
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '---';
    
    // Extract UTC date components
    const month = String(d.getUTCMonth() + 1).padStart(2, '0');
    const day = String(d.getUTCDate()).padStart(2, '0');
    const year = d.getUTCFullYear();
    
    // Extract UTC time components
    let hours = d.getUTCHours();
    const minutes = String(d.getUTCMinutes()).padStart(2, '0');
    
    // Convert to 12-hour format with AM/PM
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12
    
    // Format: MM/DD/YYYY, H:MM AM/PM (no leading zero for hours, no seconds)
    return `${month}/${day}/${year}, ${hours}:${minutes} ${ampm}`;
  }

  /**
   * Load error messages and placeholder messages dynamically from API
   */
  private loadDropdownDataMessages(): void {
    forkJoin([
      this.httpService.get('/domain/lookup?requestType=MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=TOAST MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=GENERIC MESSAGES'),
      this.httpService.get('/domain/lookup?requestType=PLACEHOLDER MESSAGES')
    ]).subscribe({
      next: (responses: any[]) => {
        this.errorMessages = responses[0].data;
        this.toastMessages = responses[1].data;
        this.genericMessages = responses[2].data;
        this.placeholderMessages = responses[3].data;
        
        // Convert arrays to key-value objects for easy access
        this.errorMessage = {};
        this.errorMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.errorMessage[message.key] = message.value;
          }
        });
        
        this.toastMessage = {};
        this.toastMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.toastMessage[message.key] = message.value;
          }
        });
        
        this.genericMessage = {};
        this.genericMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.genericMessage[message.key] = message.value;
          }
        });
        
        this.placeholderMessage = {};
        this.placeholderMessages.forEach(message => {
          if (message.key && message.value !== undefined) {
            this.placeholderMessage[message.key] = message.value;
          }
        });
        
        // Initialize date range fields with dynamic placeholders
        this.initializeDateRangeFields();
        
        // Force change detection to update the child component's config
        this.cd.detectChanges();
        
        // Subscribe to form changes AFTER the child component rebuilds its form
        // Use setTimeout to ensure the child's ngOnChanges has completed
        setTimeout(() => {
          this.subscribeToFormChanges();
        }, 0);
      },
      error: (error: any) => {
        // Fallback to default configuration if API call fails
        this.initializeDateRangeFields();
        
        // Subscribe to form changes even if API fails
        setTimeout(() => {
          this.subscribeToFormChanges();
        }, 0);
      }
    });
  }

  /**
   * Initialize date range fields configuration with dynamic placeholders
   */
  private initializeDateRangeFields(): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    this.dateRangeFields = [
      { 
        label: 'Start Date', 
        fieldName: 'startDate', 
        datatype: 'date', 
        placeholder: this.placeholderMessage['startDate'] || 'Select Start Date',
        showCalendarIcon: true, 
        showIcon: true, 
        showOnFocus: true, 
        readonly: true, 
        disabled: false,
        validation: ['required'],
        maxDate: today,
        defaultValue: null
      },
      { 
        label: 'End Date', 
        fieldName: 'endDate', 
        datatype: 'date', 
        placeholder: this.placeholderMessage['endDate'] || 'Select End Date',
        showCalendarIcon: true, 
        showIcon: true, 
        showOnFocus: true, 
        readonly: true, 
        disabled: false,
        validation: ['required'],
        maxDate: today,
        defaultValue: null
      }
    ];
  }

  /**
   * Download Excel export
   */
  downloadExcel() {
    const visibleCols = this.allColumns.filter(col => this.visibleColumns[col.key]);
    if (visibleCols.length === 0) {
      window.alert('No columns selected for export');
      return;
    }

    if (
      this.dataGridComponent &&
      this.dataGridComponent.dataGrid &&
      this.dataGridComponent.dataGrid.instance &&
      typeof this.dataGridComponent.exportFilteredDataToExcel === 'function'
    ) {
      const columns = visibleCols.map(col => ({
        label: col.caption,
        key: col.key,
        dataType: col.dataType
      }));
      const sheetName = 'PaymentReconciliationReport.xlsx';
      const gridInstance = this.dataGridComponent.dataGrid.instance;
      const oldPageSize = gridInstance.pageSize ? gridInstance.pageSize() : null;
      const totalCount = gridInstance.totalCount ? gridInstance.totalCount() : null;

      if (oldPageSize && totalCount && oldPageSize < totalCount) {
        gridInstance.pageSize(totalCount);
        setTimeout(() => {
          this.dataGridComponent.exportFilteredDataToExcel(columns, sheetName);
          setTimeout(() => {
            gridInstance.pageSize(oldPageSize);
          }, 500);
        }, 200);
        return;
      }

      this.dataGridComponent.exportFilteredDataToExcel(columns, sheetName);
      return;
    }

    const rows = Array.isArray(this.transactionTableData) ? this.transactionTableData : [];
    if (rows.length === 0) {
      window.alert('No data to export');
      return;
    }

    const exportRows = rows.map(row => {
      const filtered: any = {};
      visibleCols.forEach(col => {
        filtered[col.key] = row[col.key];
      });
      return filtered;
    });

    const exportColumns = visibleCols.map(col => ({
      label: col.caption,
      key: col.key,
      dataType: col.dataType
    }));

    const sheetName = 'PaymentReconciliationReport.xlsx';
    this.commonService.exportToExcel(exportRows, exportColumns, sheetName);
  }

  /**
   * Close column chooser dialog
   */
  closeColumnChooser() {
    this.showColumnChooser = false;
  }

  /**
   * Get count of selected/visible columns
   */
  getSelectedColumnsCount(): number {
    return Object.values(this.visibleColumns).filter(v => v).length;
  }

  /**
   * Handle column click in column chooser
   */
  onColumnClick(col: any) {
    const key = col.key || col.dataField;
    
    // Prevent changing disabled/mandatory columns
    if (col.disabled) {
      return;
    }
    
    // Prevent deselecting the last visible column
    if (this.getSelectedColumnsCount() === 1 && this.visibleColumns[key]) {
      return; // Cannot deselect the last column
    }
    
    this.visibleColumns = { ...this.visibleColumns, [key]: !this.visibleColumns[key] };
    this.updateGridConfig();
    this.cd.detectChanges();
  }

  /**
   * Handle column key press in column chooser
   */
  onColumnKey(event: any, col: any) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      
      // Prevent changing disabled/mandatory columns
      if (col.disabled) {
        return;
      }
      
      const key = col.key || col.dataField;
      // Prevent deselecting the last visible column
      if (this.getSelectedColumnsCount() === 1 && this.visibleColumns[key]) {
        return; // Cannot deselect the last column
      }
      
      this.onColumnClick(col);
    }
  }

  /**
   * Update grid configuration with visible columns
   */
  private updateGridConfig() {
    if (this.transactionTableData && this.transactionTableData.length > 0) {
      const visibleHeaders = this.transactionHeaders.filter(
        col => this.visibleColumns[col.dataField]
      );
      
      this.transactionGridConfig = DataGridVO.create({
        customHeaders: visibleHeaders,
        data: this.transactionTableData,
        showActionsColumn: false,
        showFiltering: true,
        showSearchRow: true,
        showPaging: true,
        customNoDataText: 'No transaction records found',
        role: 'payment-reconciliation',
        activeTab: 'default',
        visibleColumns: this.visibleColumns
      });
      
      this.cd.detectChanges();
    }
  }
}